import createAPIClient from '../services/apiClient'
import axios from 'axios'

export type AssistantAccessGrant = {
  accessToken: string
  expiresAt: number
}

export type AssistantChatRequest = {
  message: string
  sessionId?: string
  history?: Array<{
    role: 'user' | 'assistant'
    content: string
  }>
  state?: AssistantState
  context?: AssistantContext
  selection?: {
    selectedFromList?: boolean
    inputType?: 'text' | 'project_select' | 'device_select' | 'tag_select' | 'confirm'
  }
}

export type AssistantState = {
  workflow?: 'none' | 'ruuvitag_install' | 'device_install'
  step?:
    | 'idle'
    | 'ask_device_type'
    | 'ask_install_target'
    | 'ask_project'
    | 'ask_device'
    | 'ask_spine_tag_mount'
    | 'ask_valve_tag_role'
    | 'confirm_device_type_mismatch'
    | 'ask_valve_customer_reference'
    | 'ask_valve_heater_details'
    | 'ask_valve_heater_name'
    | 'ask_valve_heater_manufacturer'
    | 'ask_valve_heater_product_code'
    | 'ask_valve_heater_power'
    | 'confirm_existing_device_installation'
    | 'confirm_use_existing_valve_tags'
    | 'ask_valve_in_tag_id'
    | 'ask_valve_out_tag_id'
    | 'ask_valve_asset_details'
    | 'edit_valve_asset_details'
    | 'edit_valve_customer_reference'
    | 'edit_valve_level'
    | 'edit_valve_lift'
    | 'ask_spine_name'
    | 'ask_spine_level'
    | 'ask_spine_lift'
    | 'ask_spine_input_cables'
    | 'ask_spine_main_distribution'
    | 'ask_spine_parent_power_supply'
    | 'ask_tag_id'
    | 'ask_asset_name'
    | 'confirm'
    | 'completed'
  deviceType?: string
  installTarget?: string
  projectId?: number
  pendingProjectId?: number
  pendingProjectName?: string
  pendingDetectedDeviceType?: string
  pendingRequestedDeviceType?: string
  pendingResolvedDeviceName?: string
  moveExistingDeviceInstallation?: boolean
  ruuviTagSpineMount?: 'attached' | 'separate'
  ruuviTagValvePosition?: 'in' | 'out'
  deviceName?: string
  deviceCode?: string
  spineName?: string
  spineLevel?: string
  spineLift?: string
  spineInputCables?: number
  spineMainDistribution?: boolean
  spineParentPowerSupply?: string
  valveCustomerReference?: string
  valveHeaterName?: string
  valveHeaterManufacturer?: string
  valveHeaterProductCode?: string
  valveHeaterPower?: string
  valveHeaterDetails?: string
  pendingValveInRuuviTagId?: string
  pendingValveOutRuuviTagId?: string
  valveUseExistingRuuviTags?: boolean
  valveInRuuviTagId?: string
  valveOutRuuviTagId?: string
  valveAssetDetails?: string
  ruuviTagId?: string
  assetName?: string
}

export type AssistantOption = {
  value: string
  label: string
}

export type AssistantDirective = {
  intent: 'install_ruuvitag' | 'install_device' | 'general'
  reply: string
  state?: AssistantState
  ui?: {
    inputType?: 'text' | 'project_select' | 'device_select' | 'tag_select' | 'confirm'
    options?: AssistantOption[]
  }
  action?: {
    type?: 'none' | 'start_ruuvitag_scan' | 'create_ruuvitag'
    payload?: {
      deviceType?: string
      installTarget?: string
      projectId?: number
      companyId?: number | null
      deviceName?: string
      ruuviTagId?: string
      assetName?: string
    }
  }
}

export type AssistantContext = {
  language?: string
  projects?: Array<{
    id: number
    name: string
    companyId: number | null
  }>
  devices?: Array<{
    name: string
    shortUUID?: string
    deviceCode?: string | null
    assetName?: string | null
    type?: string | null
    label?: string
  }>
  discoveredTags?: Array<{
    id: string
    rssi?: number | null
  }>
}

export type AssistantChatResponse = {
  message: string
  sessionId: string
  assistant?: AssistantDirective
}

export type AssistantImageReadResponse = {
  deviceCode: string | null
  confidence: 'high' | 'medium' | 'low'
  message: string
}

export type AssistantHeaterLabelReadResponse = {
  textLines: string[]
  parsed: {
    sourceLine: string | null
    typeCode: string | null
    serialNumber: string | null
    family: 'thermo_fan' | null
    familyLabel: string | null
    heatingPowerKw: number | null
    heatingMedium: 'hot_water' | null
    heatingMediumLabel: string | null
    fanControl: 'inverter_controlled' | null
    fanControlLabel: string | null
  }
  catalogProduct: {
    id: string
    name: string
    manufacturer: string
    manufacturerProductCode: string | null
    heatingPowerKw: number | null
  } | null
  confidence: 'high' | 'medium' | 'low'
  matchReasons: string[]
  message: string
}

const api = createAPIClient('v2')

export function registerAssistantAccess(
  readAccessToken: () => string | null,
  onPinRequired: () => void
): () => void {
  const requestId = api.interceptors.request.use((config) => {
    if (config.url !== '/assistant/pin/unlock') {
      config.headers = {
        ...config.headers,
        'X-AI-Agent-Access': readAccessToken() ?? ''
      }
    }

    return config
  })
  const responseId = api.interceptors.response.use(
    (response) => response,
    (error: unknown) => {
      if (
        axios.isAxiosError(error) &&
        error.response?.status === 403 &&
        error.response.data?.error === 'AI_AGENT_PIN_REQUIRED'
      ) {
        onPinRequired()
      }

      return Promise.reject(error)
    }
  )

  return () => {
    api.interceptors.request.eject(requestId)
    api.interceptors.response.eject(responseId)
  }
}

export async function verifyAssistantPin(
  pinCode: string,
  signal: AbortSignal
): Promise<AssistantAccessGrant> {
  const response = await api.post('/assistant/pin/unlock', { pinCode }, { signal })
  return response.data
}

export const chatWithAssistant = async (
  input: AssistantChatRequest
): Promise<AssistantChatResponse> => {
  const response = await api.post('/assistant/chat', input)

  return response.data
}

export const readDeviceCodeWithAI = async (
  imageFile: File
): Promise<AssistantImageReadResponse> => {
  const formData = new FormData()
  formData.append('image', imageFile)

  const response = await api.post('/assistant/read-device-code-ai', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })

  return response.data
}

export const readHeaterLabelWithAI = async (
  imageFile: File
): Promise<AssistantHeaterLabelReadResponse> => {
  const formData = new FormData()
  formData.append('image', imageFile)

  const response = await api.post('/assistant/read-heater-label-ai', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })

  return response.data
}

export default {
  chatWithAssistant,
  readDeviceCodeWithAI,
  readHeaterLabelWithAI
}
